// Verlassene Sonden im Feld — deterministisch, aus einer **eigenen** Folge.
//
// **Wortgleiche Entsprechung von `backend/farmining/world/probes.py`.**
//
// Sehr seltene prozedurale Objekte: beim Bergen geben sie Erfahrung und ein
// Daten-Log, nie Erz. Dasselbe Muster wie Gegner und Anomalien: eigener Salt,
// eigener ID-Raum (`p:cx:cy:0`), eigene Ziehungsfolge — die Brockenfolge
// bleibt unveraendert (Gates FG-20, FG-30).
//
// Welches Log eine Sonde traegt, entscheidet dieselbe Ziehungsfolge — beide
// Seiten zeigen deterministisch denselben Schnipsel.

import { balancing, zoneAtDist, zonesFor } from '../data/balancing';
import { jsRound } from '../core/math';
import { hash3, rngFrom } from '../core/rng';
import { eraSeed } from './field';

/** Sekunden, bis eine geborgene Sonde zurueckkehrt — Spiegel von `PROBE_RESPAWN`. */
const PROBE_RESPAWN = 3600;

/** Eine Sonde, wie sie beide Seiten unabhaengig ausrechnen. */
export interface Probe {
  id: string;
  logId: string;
  x: number;
  y: number;
  r: number;
  maxHp: number;
  xp: number;
  zoneIdx: number;
  /** Laufender Zustand — nicht Teil der Erzeugung. */
  hp: number;
}

/** Die Sonden eines Chunks. **Die Reihenfolge der Ziehungen ist Vertrag.** */
export function probesInChunk(
  cx: number,
  cy: number,
  seed: number,
  era: number = balancing().eraFirst,
): Probe[] {
  const world = balancing().world;
  const params = balancing().probes;
  const welt = eraSeed(seed, era);
  const rng = rngFrom(hash3(hash3(cx, cy, welt), params.salt, welt));

  if (rng() >= params.chunkChance) return [];                           // 1

  const x = jsRound((cx + rng()) * world.chunk);                        // 2
  const y = jsRound((cy + rng()) * world.chunk);                        // 3
  const pick = rng();                                                   // 4

  const dist = Math.hypot(x, y);
  if (dist < world.clearRadius * 2) return [];

  const zoneIdx = zoneAtDist(dist, era);
  if (zoneIdx < params.minZone) return [];

  const zone = zonesFor(era)[zoneIdx];
  const logs = balancing().probeLogs;
  const log = logs[Math.min(logs.length - 1, Math.floor(pick * logs.length))];
  const maxHp = jsRound(params.hp * zone.hp);
  return [{
    id: `p:${cx}:${cy}:0`,
    logId: log.id,
    x, y,
    r: params.r,
    maxHp,
    xp: jsRound(params.xp * zone.xp),
    zoneIdx,
    hp: maxHp,
  }];
}

/**
 * Die Sonden im Umkreis — laufender Zustand, nicht neu erzeugt.
 *
 * Solo ist der Client seine eigene Autoritaet (`store.authoritative`), online
 * bucht der Server und schickt Abweichungen — dasselbe Muster wie
 * `HostileField`.
 */
export class ProbeField {
  private readonly live = new Map<string, Probe>();
  private readonly loaded = new Set<string>();
  /** Geborgene Sonden mit ihrem Rueckkehrzeitpunkt. */
  private readonly gone = new Map<string, number>();
  private time = 0;

  constructor(
    private seed: number,
    /** Die Ära des Feldes — wie bei `World`, nicht je Abfrage. */
    private era: number = balancing().eraFirst,
  ) {}

  reset(seed: number, era: number = balancing().eraFirst): void {
    this.seed = seed;
    this.era = era;
    this.live.clear();
    this.loaded.clear();
    this.gone.clear();
    this.time = 0;
  }

  all(): IterableIterator<Probe> {
    return this.live.values();
  }

  byId(id: string): Probe | undefined {
    return this.live.get(id);
  }

  /** Chunks um das Schiff herum nachladen und Zurueckgekehrte auffuellen. */
  update(dt: number, x: number, y: number): void {
    this.time += dt;
    const world = balancing().world;
    const cx = Math.floor(x / world.chunk);
    const cy = Math.floor(y / world.chunk);
    const radius = world.loadRadius;

    for (let ix = cx - radius; ix <= cx + radius; ix++) {
      for (let iy = cy - radius; iy <= cy + radius; iy++) {
        const key = `${ix}:${iy}`;
        if (this.loaded.has(key)) continue;
        this.loaded.add(key);
        for (const probe of probesInChunk(ix, iy, this.seed, this.era)) {
          if (!this.live.has(probe.id)) this.live.set(probe.id, probe);
        }
      }
    }

    for (const [id, at] of this.gone) {
      if (this.time < at) continue;
      this.gone.delete(id);
      const probe = this.live.get(id);
      if (probe) probe.hp = probe.maxHp;
    }
  }

  alive(probe: Probe): boolean {
    return probe.hp > 0;
  }

  /** Eine Sonde treffen. Gibt zurueck, ob sie dabei geborgen wurde. */
  damage(probe: Probe, amount: number): boolean {
    if (probe.hp <= 0) return false;
    probe.hp -= amount;
    if (probe.hp > 0) return false;
    probe.hp = 0;
    this.gone.set(probe.id, this.time + PROBE_RESPAWN);
    return true;
  }

  /** Eine Abweichung vom Server uebernehmen (online). */
  applyServerDelta(id: string, hp: number, dead: boolean): Probe | undefined {
    const probe = this.live.get(id);
    if (!probe) return undefined;
    probe.hp = dead ? 0 : hp;
    if (dead) this.gone.set(id, this.time + PROBE_RESPAWN);
    return probe;
  }
}
